import React from "react";

import { RealEstateResultCard } from "@features/real-estate/components/result-card";
import { RealEstateScenarioCompareTable } from "@features/real-estate/components/scenario-compare-table";
import { formatCurrency, formatPercent } from "@features/real-estate/components/format";
import { computeScenarioCollection } from "@features/real-estate/engine";
import { useRealEstateWorkspace } from "@features/real-estate/state/workspace";

export default function RealEstateResultsScreen(): React.ReactElement {
  const { state, dispatch } = useRealEstateWorkspace();
  const computedScenarios = React.useMemo(() => computeScenarioCollection(state.scenarios), [state.scenarios]);
  const baseScenario = computedScenarios[0];
  const [copyState, setCopyState] = React.useState<string | null>(null);

  const summaryText = React.useMemo(() => buildSummaryText(state, computedScenarios), [state, computedScenarios]);

  const handleCopySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopyState("Summary copied");
    } catch {
      setCopyState("Clipboard write failed");
    }
  };

  const handleDownloadJSON = () => {
    const payload = {
      schemaVersion: "real-estate-workspace/v1",
      exportedAt: new Date().toISOString(),
      intake: state.intake,
      enrichment: state.enrichment,
      scenarios: state.scenarios,
      results: computedScenarios,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "real-estate-underwrite.json";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const warningList = [
    ...(state.enrichment?.warnings || []),
    ...computedScenarios.flatMap((scenario) => scenario.warnings),
  ];

  return (
    <section className="re-screen">
      <div className="re-screen-header">
        <div>
          <h2>Results</h2>
          <p>Everything below is derived from the in-memory workspace. Refreshing the browser clears it.</p>
        </div>
        <div className="re-action-row">
          <button type="button" className="re-button" onClick={handleCopySummary}>Copy summary</button>
          <button type="button" className="re-button" onClick={handleDownloadJSON}>Download JSON</button>
          <button type="button" className="re-button re-button-danger" onClick={() => dispatch({ type: "reset_workspace" })}>Reset</button>
        </div>
      </div>

      {copyState ? <div className="re-callout">{copyState}</div> : null}

      <div className="re-results-grid">
        <RealEstateResultCard label="Cap rate" value={formatPercent(baseScenario?.metrics.capRate)} />
        <RealEstateResultCard label="Cash-on-cash" value={formatPercent(baseScenario?.metrics.cashOnCashReturn)} />
        <RealEstateResultCard label="DSCR" value={baseScenario?.metrics.dscr === null || baseScenario?.metrics.dscr === undefined ? "N/A" : baseScenario.metrics.dscr.toFixed(2)} />
        <RealEstateResultCard label="Monthly cash flow" value={formatCurrency(baseScenario?.metrics.monthlyCashFlow)} tone={(baseScenario?.metrics.monthlyCashFlow || 0) >= 0 ? "good" : "warn"} />
        <RealEstateResultCard label="Cash to close" value={formatCurrency(baseScenario?.metrics.cashToClose)} />
        <RealEstateResultCard label="Levered IRR" value={formatPercent(baseScenario?.metrics.leveredIRR)} />
      </div>

      {warningList.length ? (
        <section className="re-panel">
          <div className="re-panel-title">Warnings</div>
          <ul className="re-warning-list">
            {warningList.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="re-panel">
        <div className="re-panel-title">Scenario comparison</div>
        <RealEstateScenarioCompareTable scenarios={computedScenarios} />
      </section>

      <section className="re-panel">
        <div className="re-panel-title">Copyable summary</div>
        <pre className="re-summary-text">{summaryText}</pre>
      </section>
    </section>
  );
}

function buildSummaryText(
  state: ReturnType<typeof useRealEstateWorkspace>["state"],
  scenarios: ReturnType<typeof computeScenarioCollection>,
): string {
  return scenarios.map(({ scenario, metrics }) => (
    [
      `${scenario.name} scenario`,
      `Address: ${state.intake.addressLine1 || "N/A"}, ${state.intake.city || "N/A"}, ${state.intake.state || "N/A"}`,
      `Purchase price: ${formatCurrency(metrics.cashToClose ? scenario.financing.purchasePrice : scenario.financing.purchasePrice)}`,
      `Gross scheduled rent: ${formatCurrency(metrics.grossScheduledRent)}`,
      `NOI: ${formatCurrency(metrics.noi)}`,
      `Monthly cash flow: ${formatCurrency(metrics.monthlyCashFlow)}`,
      `Cap rate: ${formatPercent(metrics.capRate)}`,
      `Cash-on-cash: ${formatPercent(metrics.cashOnCashReturn)}`,
      `DSCR: ${metrics.dscr === null || metrics.dscr === undefined ? "N/A" : metrics.dscr.toFixed(2)}`,
      `Levered IRR: ${formatPercent(metrics.leveredIRR)}`,
      `Cash to close: ${formatCurrency(metrics.cashToClose)}`,
    ].join("\n")
  )).join("\n\n");
}
